import React, { useState } from 'react'

import { useAnalysisGenerationActions } from 'redux/actions/AnalysisGenerations'
import { useSnackbarActions } from 'redux/actions/Snackbar'
import { useIsMobile } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Text from 'components_new/atoms/Text'

import ContentBox from 'components_new/molecules/ContentBox'

import DataDialog from 'components_new/organisms/DataDialog'
import RerunAnalysisDialog from 'components_new/organisms/dialogs/RerunAnalysisDialog'
import { useAnalysisDialog } from 'components_new/organisms/DataDialog/useAnalysisDialog'
import AnalysisSelection from 'components_new/organisms/analyses/AnalysisSelection'
import AnalysisTrend from 'components_new/organisms/analyses/AnalysisTrend'
import { TAnalysis } from 'redux/hooks/Analyses'
import StatusBox from 'components_new/molecules/StatusBox'
import { EAnalysisStatus } from 'types/Enums'
import { SnackbarMessage } from 'types/GlobalSnackbar'

interface Version2Props {
  analysis: TAnalysis
}

/** The structured report: method, the selection's trend, the selection. */
const Version2 = ({ analysis }: Version2Props) => {
  const isMobile = useIsMobile()
  const { t } = useTranslation()

  const explore = useAnalysisDialog(analysis)
  const aggregated = analysis.aggregated_values
  const method = analysis.meta?.method ?? []

  // Which real estates the underlying data is open for, if any.
  const [exploring, setExploring] = useState<string[] | null>(null)

  // The property about to be excluded. The run then shows on the page.
  const [excluding, setExcluding] = useState<string | null>(null)

  const { tryRerunAnalysis } = useAnalysisGenerationActions()
  const { toggleSnackbarOpen } = useSnackbarActions()

  const handleRerun = (callback: () => void) => {
    if (!excluding) return

    const excluded = [
      ...(analysis.analysisType?.excluded_real_estates ?? []),
      excluding
    ]

    tryRerunAnalysis(analysis.id, excluded, (err) => {
      callback()

      if (err) {
        toggleSnackbarOpen(SnackbarMessage.AnalysisRerunFailed)

        return
      }

      setExcluding(null)
    })
  }

  return (
    <>
      {method.length > 0 ? (
        <Box sx={{ maxWidth: 'content', mb: 3 }}>
          {method.map((paragraph, i) => (
            <Text key={i} sx={{ mb: i < method.length - 1 ? 1 : 0 }}>
              {paragraph}
            </Text>
          ))}
        </Box>
      ) : null}

      {aggregated ? (
        <AnalysisTrend
          calculation={aggregated.calculation}
          comparativeValues={aggregated.comparative_values}
          id={analysis.id}
          potential={aggregated.potential}
          qualifiedCount={aggregated.qualified_count}
          result={analysis.meta?.result ?? null}
          segments={aggregated.segments}
          selectedCount={aggregated.selected_count}
          startValue={aggregated.start_value}
          sx={{ mb: 3 }}
          targetValue={aggregated.target_value}
          values={aggregated.values}
        />
      ) : null}

      <ContentBox
        border={!isMobile}
        disablePadding={isMobile}
        header={{ title: t('analyses', 'selection') }}
        id={'selection'}
      >
        <AnalysisSelection
          id={analysis.id}
          onExclude={analysis.analysis_type_id ? setExcluding : undefined}
          onExplore={(name) => setExploring([name])}
        />

        {analysis.status !== EAnalysisStatus.UNHANDLED ? (
          <StatusBox severity="info" title={t('analyses', 'selection_locked')}>
            {analysis.status === EAnalysisStatus.ACCEPTED
              ? t('analyses', 'selection_locked_accepted')
              : t('analyses', 'selection_locked_rejected')}
          </StatusBox>
        ) : null}
      </ContentBox>

      <DataDialog
        {...explore.dialogProps(exploring)}
        onClose={() => setExploring(null)}
      />

      <RerunAnalysisDialog
        alreadyExcluded={analysis.analysisType?.excluded_real_estates ?? []}
        name={excluding}
        onClose={() => setExcluding(null)}
        onConfirm={handleRerun}
      />
    </>
  )
}

export default Version2
